import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Droplets, AlertCircle, CheckCircle, Clock, ChevronDown, Thermometer, CloudRain, Sun } from 'lucide-react';
import { TrustBadge } from '@/components/TrustBadge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = {
  cream: '#FAF6EC', forest: '#1A4331', forestDeep: '#0F2D1F', leaf: '#6B9F4A', leafDeep: '#4E7A33',
  gold: '#C9A85B', sky: '#8AB4F8', text: '#2A3528', muted: '#5C665C', white: '#FFFFFF',
};
const CARD_SH = '0 6px 16px -2px rgba(26,67,49,0.12)';
const V = {
  h1: { fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 32, lineHeight: '38px', color: C.forest },
  h3: { fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: 19, lineHeight: '25px', color: C.forest },
  title: { fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: 18, lineHeight: '24px', color: C.forest },
  bodySemi: { fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 15, lineHeight: '22px', color: C.text },
  body: { fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: 14.5, lineHeight: '23px', color: C.text },
  small: { fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: 13, lineHeight: '19px', color: C.muted },
  label: { fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 13, lineHeight: '18px', color: C.muted },
};

const REC = {
  water_today: { label: 'Water today', Icon: Droplets, bg: 'rgba(138,180,248,0.9)', soft: 'rgba(138,180,248,0.14)', fg: '#2E5E96' },
  skip: { label: 'Skip today', Icon: CheckCircle, bg: 'rgba(107,159,74,0.9)', soft: 'rgba(107,159,74,0.14)', fg: C.leafDeep },
  check: { label: 'Check soil first', Icon: AlertCircle, bg: 'rgba(201,168,91,0.92)', soft: 'rgba(201,168,91,0.16)', fg: '#8A6D2B' },
};
const recOf = (r) => REC[r] || REC.check;

const PRINCIPLES = [
  ['Water deeply, not frequently.', 'Deep watering encourages deeper, more resilient roots.'],
  ['Morning is best.', 'Less evaporation and leaves dry quickly, preventing disease.'],
  ['Check the soil.', 'Insert a finger 5cm deep — if dry, water; if moist, wait.'],
  ['Mulch heavily.', '5cm of mulch can cut water needs by around half.'],
  ['Water at the base.', 'Avoid wetting leaves to prevent fungal disease.'],
];

function WateringAdvisor({ user }) {
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openWhy, setOpenWhy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/watering/advice`, { withCredentials: true })
      .then((r) => setAdvice(r.data)).catch((e) => console.error(e)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const w = advice?.weather;
  const crops = advice?.crops || [];

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div>
          <h1 style={V.h1}>Watering</h1>
          <p style={V.small} className="mt-1">Smart watering based on weather, your crops and timing</p>
        </div>

        {/* Strategy hero */}
        <div className="relative overflow-hidden" style={{ borderRadius: 24, boxShadow: CARD_SH, background: `linear-gradient(135deg, ${C.forest}, ${C.forestDeep})` }} data-testid="general-advice">
          <div className="absolute -top-10 -right-6 w-48 h-48 rounded-full pointer-events-none" style={{ background: 'rgba(138,180,248,0.25)' }} />
          <div className="relative" style={{ padding: 24 }}>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(138,180,248,0.22)' }}><Droplets className="w-6 h-6" style={{ color: C.sky }} /></span>
              <h2 style={{ ...V.h3, color: C.cream }}>Today&apos;s watering strategy</h2>
            </div>
            <p style={{ ...V.body, color: 'rgba(250,246,236,0.92)' }}>{advice?.general_advice}</p>
            {w && (
              <div className="grid grid-cols-3 gap-3 mt-5">
                <WChip Icon={Thermometer} label="Now" value={`${Math.round(w.current_temp)}°`} />
                <WChip Icon={Sun} label="High" value={`${Math.round(w.max_temp)}°`} />
                <WChip Icon={CloudRain} label="3-day rain" value={`${(w.precipitation_3day ?? 0).toFixed(1)}mm`} />
              </div>
            )}
          </div>
        </div>

        {crops.length > 0 ? (
          <div>
            <h2 style={V.h3} className="mb-3">Your crops</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
              {crops.map((crop) => {
                const rec = recOf(crop.recommendation);
                const Icon = rec.Icon;
                const isOpen = openWhy === crop.planting_id;
                return (
                  <div key={crop.planting_id} data-testid={`watering-crop-${crop.planting_id}`} style={{ background: C.cream, borderRadius: 20, padding: 18, boxShadow: CARD_SH }}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 style={V.title}>{crop.crop_name}</h3>
                        <p style={V.small} className="capitalize">{crop.stage}</p>
                      </div>
                      <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: rec.soft }}><Icon className="w-5 h-5" style={{ color: rec.fg }} /></span>
                    </div>
                    <span className="inline-block rounded-full mb-3" style={{ background: rec.bg, color: '#fff', padding: '4px 12px', ...V.label, fontSize: 12, color: '#fff' }}>{rec.label}</span>
                    <p style={{ ...V.body, marginBottom: 8 }}>{crop.reason}</p>
                    <div className="flex items-center gap-2" style={{ ...V.small, paddingTop: 10, borderTop: '1px solid rgba(26,67,49,0.08)' }}>
                      <Clock className="w-3.5 h-3.5" style={{ color: C.muted }} />
                      <span>Best time: {crop.best_time}</span>
                    </div>
                    {crop.why && (
                      <>
                        <button onClick={() => setOpenWhy(isOpen ? null : crop.planting_id)} data-testid={`watering-why-${crop.planting_id}`} className="mt-3 flex items-center gap-1.5">
                          {crop.confidence && <span className="rounded-full" style={{ background: 'rgba(107,159,74,0.14)', color: C.leafDeep, padding: '2px 9px', ...V.label, fontSize: 11 }}>{crop.confidence}</span>}
                          <span style={{ ...V.label, fontSize: 12, color: C.leafDeep }}>{isOpen ? 'Hide' : 'Why?'}</span>
                          <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{ color: C.leafDeep, transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                        </button>
                        {isOpen && <p className="mt-2 rounded-2xl" style={{ ...V.small, background: C.white, padding: 12 }}>{crop.why}</p>}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="relative overflow-hidden text-center" style={{ borderRadius: 22, boxShadow: CARD_SH, background: C.cream, padding: '48px 24px' }}>
            <Droplets className="w-14 h-14 mx-auto mb-3" style={{ color: C.sky }} />
            <h3 style={V.h3}>No active crops to water</h3>
            <p style={V.small} className="mt-1 mb-6">Add some crops to get personalised watering advice</p>
            <a href="/crops" className="inline-block font-semibold rounded-full px-8 py-3" style={{ background: C.forest, color: C.cream }}>Browse the Handbook</a>
          </div>
        )}

        {/* Principles */}
        <div style={{ background: C.cream, borderRadius: 20, padding: 20, boxShadow: CARD_SH }}>
          <h3 style={V.h3} className="mb-3">Smart watering principles</h3>
          <div className="space-y-2.5">
            {PRINCIPLES.map(([t, d], i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="rounded-full flex-shrink-0 mt-1.5" style={{ width: 6, height: 6, background: C.leaf }} />
                <p style={V.body}><strong style={{ color: C.forest }}>{t}</strong> {d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function WChip({ Icon, label, value }) {
  return (
    <div className="rounded-2xl flex flex-col items-center gap-1" style={{ background: 'rgba(250,246,236,0.1)', padding: 12 }}>
      <Icon className="w-4 h-4" style={{ color: 'rgba(250,246,236,0.75)' }} />
      <span style={{ ...V.label, fontSize: 11, color: 'rgba(250,246,236,0.7)' }}>{label}</span>
      <span style={{ ...V.bodySemi, color: C.cream }}>{value}</span>
    </div>
  );
}

export default WateringAdvisor;
